import { db } from '../config/firebase'
import { doc, getDoc, setDoc, updateDoc, increment, collection, query, where, getDocs } from 'firebase/firestore'

/**
 * Gera um código único de indicação para o usuário
 */
export function generateReferralCode(userId) {
  // Usa os primeiros 8 caracteres do userId + 4 caracteres aleatórios
  const shortId = userId.substring(0, 8)
  const random = Math.random().toString(36).substring(2, 6).toUpperCase()
  return `${shortId}-${random}`
}

/**
 * Cria ou obtém dados de indicação do usuário
 */
export async function getUserReferralData(userId) {
  try {
    const referralRef = doc(db, 'referrals', userId)
    const referralDoc = await getDoc(referralRef)
    
    if (referralDoc.exists()) {
      return referralDoc.data()
    }
    
    // Cria novo registro de indicação
    const referralCode = generateReferralCode(userId)
    const newReferralData = {
      userId,
      referralCode,
      referredBy: null,
      referralCount: 0,
      gamesUnlockedByReferral: 0,
      referredUsers: [],
      createdAt: new Date()
    }
    
    await setDoc(referralRef, newReferralData)
    return newReferralData
  } catch (error) {
    console.error('Error getting referral data:', error)
    return null
  }
}

/**
 * Processa uma nova indicação
 */
export async function processReferral(newUserId, referralCode) {
  try {
    if (!referralCode) {
      console.log('No referral code provided')
      return null
    }
    
    console.log('Processing referral for user:', newUserId, 'with code:', referralCode)
    
    // Verifica se o usuário já foi referenciado antes
    const newUserRef = doc(db, 'referrals', newUserId)
    const newUserDoc = await getDoc(newUserRef)
    
    if (newUserDoc.exists() && newUserDoc.data().referredBy) {
      console.log('User already referred by:', newUserDoc.data().referredBy)
      return null
    }
    
    // Busca quem tem esse código de indicação
    const referralsQuery = query(
      collection(db, 'referrals'),
      where('referralCode', '==', referralCode.toUpperCase())
    )
    
    const snapshot = await getDocs(referralsQuery)
    
    if (snapshot.empty) {
      console.log('Referral code not found:', referralCode)
      return null
    }
    
    const referrerDoc = snapshot.docs[0]
    const referrerId = referrerDoc.id
    const referrerData = referrerDoc.data()
    
    console.log('Found referrer:', referrerId)
    
    // Não pode se auto-indicar
    if (referrerId === newUserId) {
      console.log('Cannot refer yourself')
      return null
    }
    
    // Marca o novo usuário como indicado
    if (newUserDoc.exists()) {
      console.log('Updating existing user referral data')
      await updateDoc(newUserRef, {
        referredBy: referrerId
      })
    } else {
      console.log('Creating new user referral data')
      await setDoc(newUserRef, {
        userId: newUserId,
        referralCode: generateReferralCode(newUserId),
        referredBy: referrerId,
        referralCount: 0,
        gamesUnlockedByReferral: 0,
        referredUsers: [],
        createdAt: new Date()
      })
    }
    
    // Atualiza contador do referenciador
    const referrerRef = doc(db, 'referrals', referrerId)
    const newCount = (referrerData.referralCount || 0) + 1
    
    console.log('Updating referrer count to:', newCount)
    
    await updateDoc(referrerRef, {
      referralCount: increment(1),
      referredUsers: [...(referrerData.referredUsers || []), newUserId]
    })
    
    // A cada 5 indicações, permite escolher um jogo
    const gamesUnlocked = Math.floor(newCount / 5)
    
    console.log('Games to unlock:', gamesUnlocked)
    
    if (gamesUnlocked > (referrerData.gamesUnlockedByReferral || 0)) {
      console.log('User can now choose a game to unlock!')
      await updateDoc(referrerRef, {
        gamesUnlockedByReferral: gamesUnlocked,
        pendingGameChoice: true
      })
      
      return {
        success: true,
        referrerId,
        newCount,
        gamesUnlocked,
        canChooseGame: true
      }
    }
    
    return {
      success: true,
      referrerId,
      newCount,
      gamesUnlocked: referrerData.gamesUnlockedByReferral || 0
    }
  } catch (error) {
    console.error('Error processing referral:', error)
    return null
  }
}

/**
 * Desbloqueia jogo escolhido por indicação
 */
export async function unlockGameByReferral(userId, gameId) {
  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    
    if (!userDoc.exists()) {
      throw new Error('User not found')
    }
    
    const userData = userDoc.data()
    const currentUnlocked = userData.unlockedGames || ['tetris']
    
    // Verifica se o jogo já está desbloqueado
    if (currentUnlocked.includes(gameId)) {
      throw new Error('Game already unlocked')
    }
    
    // Adiciona o jogo escolhido
    const newUnlockedGames = [...currentUnlocked, gameId]
    
    await updateDoc(userRef, {
      unlockedGames: newUnlockedGames
    })
    
    // Remove o flag de escolha pendente
    const referralRef = doc(db, 'referrals', userId)
    await updateDoc(referralRef, {
      pendingGameChoice: false
    })
    
    console.log(`Unlocked game ${gameId} by referral for ${userId}`)
    return { success: true, unlockedGames: newUnlockedGames }
  } catch (error) {
    console.error('Error unlocking game by referral:', error)
    throw error
  }
}

/**
 * Obtém jogos disponíveis para desbloquear
 */
export async function getAvailableGamesForUnlock(userId) {
  try {
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)
    
    if (!userDoc.exists()) {
      return []
    }
    
    const userData = userDoc.data()
    const currentUnlocked = userData.unlockedGames || ['tetris']
    
    const allGames = [
      { id: 'tetris', name: 'Tetris', emoji: '🟦' },
      { id: 'snake', name: 'Snake', emoji: '🐍' },
      { id: 'pong', name: 'Pong', emoji: '🏓' },
      { id: 'breakout', name: 'Breakout', emoji: '🧱' },
      { id: 'sancode', name: 'San Code', emoji: '👾' }
    ]
    
    // Retorna apenas jogos que ainda não estão desbloqueados
    return allGames.filter(game => !currentUnlocked.includes(game.id))
  } catch (error) {
    console.error('Error getting available games:', error)
    return []
  }
}

/**
 * Gera link de indicação
 */
export function generateReferralLink(referralCode) {
  const baseUrl = window.location.origin
  return `${baseUrl}?ref=${referralCode}`
}

/**
 * Obtém código de indicação da URL
 */
export function getReferralCodeFromUrl() {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('ref')
}

/**
 * Compartilha link de indicação
 */
export async function shareReferralLink(referralCode) {
  const link = generateReferralLink(referralCode)
  const text = `Jogue jogos clássicos retrô no San Dev Code! Use meu código de indicação: ${referralCode}`
  
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'San Dev Code - Retro Games',
        text: text,
        url: link
      })
      return true
    } catch (error) {
      console.log('Share cancelled or failed')
    }
  }
  
  // Fallback: copia para clipboard
  try {
    await navigator.clipboard.writeText(link)
    return 'copied'
  } catch (error) {
    console.error('Failed to copy:', error)
    return false
  }
}
